import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, waLink } from "../lib/api";
import BirthDetailsForm from "../components/BirthDetailsForm";
import { Button } from "../components/ui/button";
import { Sparkles, MessageCircle, GraduationCap, ArrowRight } from "lucide-react";
import Seo from "../lib/Seo";
import { toast } from "sonner";

const NAK_FAQ = [
  { q: "What is Janam Nakshatra?", a: "Janam Nakshatra is the lunar mansion — one of 27 divisions of the zodiac — occupied by the Moon at the exact moment of your birth. Vedic astrology treats it as the deepest personal marker of your emotional and karmic nature." },
  { q: "How is Janam Nakshatra calculated?", a: "By converting your civil birth time to Universal Time, then computing the sidereal longitude of the Moon using Swiss Ephemeris. The result is mapped to one of the 27 nakshatras (each 13°20′) and further to one of four padas." },
  { q: "Why is exact birth time important?", a: "The Moon moves about 13° per day, so a difference of 30 minutes can change your pada and even your nakshatra near a boundary." },
  { q: "What's the difference between Rashi and Nakshatra?", a: "Rashi is the Moon-sign (one of 12), a broad indicator. Nakshatra is a much finer 27-fold division within the same zodiac, revealing the specific quality of that Moon placement." },
  { q: "What is a Pada?", a: "Each Nakshatra is divided into 4 quarters called Padas (3°20′ each). Pada aligns with the Navamsa (D9) and refines nakshatra interpretation." },
];

export default function JanamNakshatra() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const handle = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post("/nakshatra/calculate", payload);
      setResult(data);
      // scroll to result
      setTimeout(() => document.getElementById("nak-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-nakshatra" className="min-h-screen">
      <Seo
        title="Free Janam Nakshatra Finder | Find Your Birth Star | YourAstroHub"
        description="Find your Janam Nakshatra, Pada, Moon Sign and Nakshatra Lord using your exact birth date, time and place. Free Vedic tool by YourAstroHub."
        keywords="Janam Nakshatra Finder, Birth Star Calculator, Vedic Nakshatra, Pada Calculator"
        path="/janam-nakshatra"
      />

      <section className="pt-10 pb-6">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <div className="eyebrow mb-3">Free Vedic Tool</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05]">Free <span className="gold-text italic">Janam Nakshatra</span> Finder</h1>
          <p className="mt-4 text-foreground/70 text-base md:text-lg">
            Find your Janam Nakshatra, Pada, Rashi and Nakshatra Lord using your exact birth details — calculated with Swiss Ephemeris using the sidereal (Lahiri) zodiac.
          </p>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <BirthDetailsForm onSubmit={handle} busy={busy} ctaLabel="Find my Nakshatra" tool="nakshatra" />
        </div>
      </section>

      {result && (
        <section id="nak-result" className="pb-16">
          <div className="max-w-3xl mx-auto px-6 md:px-8">
            <div data-testid="nak-result-card" className="rounded-3xl p-8 md:p-12 border-2 border-[#D4AF37]/50 bg-[#0B1F3A] text-[#F5E8D8] text-center relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#D4AF37]/15 rounded-full blur-3xl" />
              <div className="relative">
                <div className="eyebrow text-[#F5E8D8]/60">Your Birth Star</div>
                <h2 className="font-serif text-5xl md:text-7xl mt-3 gold-text">{result.nakshatra.name}</h2>
                <div className="mt-2 text-lg text-[#F5E8D8]/70">{result.nakshatra.sanskrit} · Pada {result.pada}</div>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Stat label="Rashi" value={result.rashi.sign} sub={result.rashi.sign_lord} />
                  <Stat label="Nakshatra Lord" value={result.nakshatra.lord} />
                  <Stat label="Moon Degree" value={`${result.moon_longitude}°`} />
                  <Stat label="Pada" value={result.pada} />
                </div>
              </div>
            </div>

            {/* Educational card */}
            <div className="mt-8 rounded-3xl border border-foreground/10 p-6 md:p-8 bg-background">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="eyebrow">Understanding {result.nakshatra.name}</div>
                  <div className="mt-1 font-serif text-2xl">{result.nakshatra.symbol}</div>
                </div>
                <div className="text-xs text-foreground/50 flex flex-wrap gap-x-3 gap-y-1">
                  <span>Deity: <b>{result.nakshatra.deity}</b></span>
                  <span>Gana: <b>{result.nakshatra.gana}</b></span>
                  <span>Yoni: <b>{result.nakshatra.yoni}</b></span>
                  <span>Element: <b>{result.nakshatra.element}</b></span>
                </div>
              </div>
              <p className="text-foreground/80 leading-relaxed">{result.nakshatra.summary}</p>
              <p className="mt-4 text-xs text-foreground/50 italic">
                Astrology is provided for informational and traditional Jyotish purposes. Please do not treat this as medical, legal, financial or professional advice.
              </p>
            </div>

            {/* CTA to full kundali */}
            <div className="mt-8 rounded-3xl p-8 border-2 border-[#D4AF37]/50 bg-background text-center">
              <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
              <h3 className="font-serif text-2xl md:text-3xl">Want to see your <span className="gold-text italic">complete Kundali?</span></h3>
              <p className="mt-2 text-foreground/70 max-w-xl mx-auto">Get your Lagna, D1 &amp; D9 charts, planetary positions and Vimshottari Dasha — all free.</p>
              <Button size="lg" onClick={() => navigate("/kundali-generator")}
                className="mt-4 rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6"
                data-testid="cta-full-kundali">
                Generate Full Kundali <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <LeadFooter leadId={result.lead_id} />
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-12 bg-[#F4EFE6]/50 dark:bg-[#0F284B]/50 border-t border-foreground/10">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <h2 className="font-serif text-3xl md:text-4xl text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {NAK_FAQ.map((f, i) => (
              <details key={i} className="rounded-2xl border border-foreground/10 bg-background p-5 group" data-testid={`nak-faq-${i}`}>
                <summary className="font-semibold cursor-pointer flex items-start justify-between gap-3 marker:hidden">
                  <span>{f.q}</span>
                  <span className="text-[#D4AF37] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/70">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#F5E8D8]/50">{label}</div>
      <div className="mt-1 font-serif text-xl md:text-2xl">{value}</div>
      {sub && <div className="text-[10px] text-[#F5E8D8]/60 mt-0.5">{sub}</div>}
    </div>
  );
}

export function LeadFooter({ leadId }) {
  const notify = (action) => {
    if (!leadId) return;
    api.post(`/astrology/lead/${leadId}/interest`, { action }).catch(() => {});
  };
  return (
    <div className="mt-8 rounded-3xl border border-foreground/10 p-6 md:p-8 bg-background">
      <div className="text-center">
        <div className="eyebrow">Next Step</div>
        <h3 className="font-serif text-2xl md:text-3xl mt-2">Want a <span className="gold-text italic">professional interpretation?</span></h3>
        <p className="mt-2 text-foreground/70 max-w-lg mx-auto">
          Your birth chart contains much more than planetary positions. Get a personalised Vedic Astrology consultation with Bhupaal Bisht.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <a href="/consultations" onClick={() => notify("consultation")}>
            <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-7 py-5" data-testid="lead-consultation">
              <Sparkles className="w-4 h-4 mr-2" />Book Consultation
            </Button>
          </a>
          <a href={waLink("I generated my chart on YourAstroHub and would like to know more about a consultation.")} target="_blank" rel="noreferrer" onClick={() => notify("whatsapp")}>
            <Button size="lg" variant="outline" className="rounded-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 px-7 py-5" data-testid="lead-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" />WhatsApp Me
            </Button>
          </a>
          <a href="/courses" onClick={() => notify("course")}>
            <Button size="lg" variant="outline" className="rounded-full px-7 py-5" data-testid="lead-course">
              <GraduationCap className="w-4 h-4 mr-2" />Explore Astrology Courses
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
